import { useState } from 'react';
import { Modal } from './ui';
import { submitReorderRequest } from '../services/reorderRequestService';
import { ApiError } from '../errors/ApiError';
import { ReorderRequestModalProps } from '../types';

export function ReorderRequestModal({ isOpen, onClose, onSuccess, productId }: ReorderRequestModalProps) {
  const [quantity, setQuantity] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const resetFields = () => {
    setQuantity('');
    setNotes('');
    setErrorMessage(null);
  };

  const handleClose = () => {
    resetFields();
    onClose();
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setErrorMessage(null);

    try {
      await submitReorderRequest({
        item_id: productId,
        quantity: quantity ? parseInt(quantity, 10) : null,
        notes: notes.trim() || null,
      });
      resetFields();
      onSuccess();
    } catch (error) {
      setErrorMessage(
        error instanceof ApiError
          ? error.message
          : 'Failed to submit request. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Request Reorder" size="sm">
      <div className="space-y-4">
        {errorMessage && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
            {errorMessage}
          </div>
        )}

        <div>
          <label htmlFor="reorderQuantity" className="block text-sm font-medium text-gray-700 mb-1">
            Requested Quantity
          </label>
          <input
            id="reorderQuantity"
            type="number"
            min={1}
            value={quantity}
            onChange={(event) => setQuantity(event.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter quantity"
          />
        </div>

        <div>
          <label htmlFor="reorderNotes" className="block text-sm font-medium text-gray-700 mb-1">
            Notes
          </label>
          <textarea
            id="reorderNotes"
            rows={3}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            placeholder="Optional notes for your account rep"
          />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? 'Submitting...' : 'Submit Request'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
